import { permissions } from '@repo/permissions'
import type { AccessRequirements } from '@repo/permissions/access'

export type LoadCreationCapabilities = {
  canCreateNormalLoad: boolean
  canDuplicate: boolean
  canEditDraft: boolean
  canOpenNewPage: boolean
  canPublishDraft: boolean
  canQuickCreate: boolean
}

export type LoadCreationPermissionFacts = {
  canCreateDraft: boolean
  canCreateNormalLoad: boolean
  canPublishDraft: boolean
}

export type LoadCreationRequest = { draftId: number; mode: 'draft' } | { mode: 'new' }

export type LoadCreationSearchParams = {
  draftId?: string | string[] | undefined
}

type LoadCreationAccessRequirement = 'createDraft' | 'createNormalLoad' | 'publishDraft'

export const loadCreationAccessRequirements = {
  createDraft: { permissions: [permissions.loads.createDraft] },
  createNormalLoad: { permissions: [permissions.loads.create] },
  publishDraft: { permissions: [permissions.loads.publishDraft] },
} as const satisfies Record<LoadCreationAccessRequirement, AccessRequirements>

export const getLoadCreationCapabilities = ({
  canCreateDraft,
  canCreateNormalLoad,
  canPublishDraft,
}: LoadCreationPermissionFacts): LoadCreationCapabilities => {
  const canEditDraft = canCreateNormalLoad || canCreateDraft

  return {
    canCreateNormalLoad,
    canDuplicate: canCreateNormalLoad,
    canEditDraft,
    canOpenNewPage: canEditDraft,
    canPublishDraft,
    canQuickCreate: canCreateNormalLoad,
  }
}

/** Whether the menus and the command palette offer duplicating a Load: never a draft. */
export const canDuplicateLoad = (
  { canDuplicate }: Pick<LoadCreationCapabilities, 'canDuplicate'>,
  statusAlias: string,
) => canDuplicate && statusAlias !== 'draft'

export const resolveLoadCreationRequest = (
  searchParams: LoadCreationSearchParams,
): LoadCreationRequest => {
  const draftId = parseLoadIdParam(searchParams.draftId)
  if (draftId) return { draftId, mode: 'draft' }

  return { mode: 'new' }
}

export const canOpenLoadDraft = ({
  canEditDraft,
  canPublishDraft,
}: Pick<LoadCreationCapabilities, 'canEditDraft' | 'canPublishDraft'>) =>
  canEditDraft || canPublishDraft

export const canAccessLoadCreationRequest = (
  request: LoadCreationRequest,
  capabilities: LoadCreationCapabilities,
): boolean => {
  switch (request.mode) {
    case 'draft':
      return canOpenLoadDraft(capabilities)
    case 'new':
      return capabilities.canOpenNewPage
  }
}

const parseLoadIdParam = (value: LoadCreationSearchParams[keyof LoadCreationSearchParams]) => {
  const rawValue = Array.isArray(value) ? value[0] : value
  if (!rawValue) return undefined

  const loadId = Number(rawValue)
  return Number.isSafeInteger(loadId) && loadId > 0 ? loadId : undefined
}
